import React, { forwardRef, useId } from "react";
import type { BaseProps } from "@purpur/common-types";
import { FieldErrorText } from "@purpur/field-error-text";
import { FieldHelperText } from "@purpur/field-helper-text";
import { IconCheckCircleFilled } from "@purpur/icon/check-circle-filled";
import { IconChevronDown } from "@purpur/icon/chevron-down";
import { Label } from "@purpur/label";
import { Spinner } from "@purpur/spinner";
import c from "classnames/bind";

import styles from "./select.module.scss";

export type SelectProps = Omit<BaseProps<"select">, "multiple" | "children"> & {
  label?: string;
  /**
   * Options which will be available in the select input's dropdown menu.
   *
   * For single options:
   *
   * ```
   * [
   *   {
   *     label: "Option 1",
   *     value: "Option 1"
   *   },
   *   {
   *     label: "Option 2",
   *     value: "Option 2"
   *   }
   * ]
   * ```
   * For option groups:
   *
   * ```
   *
   * [
   *   {
   *     label: "Option group 1"
   *     options: [
   *       {
   *         label: "Group 1 option 1",
   *         value: "group 1 option 1"
   *       },
   *       {
   *         label: "Group 1 option 2",
   *         value: "Group 1 option 2"
   *       }
   *     ]
   *   }
   * ]
   * ```
   *
   * _Note! When no value is provided for the option, the label of the option is used as a fallback._
   */
  options: (SelectOption | SelectOptionGroup)[];
  /**
   * Placeholder option for the dropdown (e.g "Select" / "Choose").
   *
   * Use the following format when providing a placeholder:
   *
   * ```
   * {
   *   label: "Placeholder option",
   *   value: "Placeholder value"
   * }
   * ```
   */
  placeholder?: SelectOption;
  readOnly?: boolean;
  errorText?: string;
  helperText?: string;
  /**
   * Use to render select with valid appearance. A check
   * icon will render at the start inside of the input.
   */
  valid?: boolean;
  loading?: boolean;
  negative?: boolean;
};

export type SelectOption = {
  label: string;
  value?: string;
};

export type SelectOptionGroup = {
  label: string;
  options: SelectOption[];
};

const cx = c.bind(styles);
const rootClassName = "purpur-select";

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      label,
      options,
      className,
      errorText,
      helperText,
      negative = false,
      placeholder,
      readOnly = false,
      ["data-testid"]: dataTestId,
      valid,
      loading,
      ...selectProps
    },
    ref
  ) => {
    const getTestId = (name: string) => (dataTestId ? `${dataTestId}-${name}` : undefined);
    const isValid = valid && !errorText;
    const randomId = useId();
    const fieldId = selectProps.id ?? randomId;

    const helperTextId = helperText ? `${fieldId}-helper-text` : undefined;

    const selectContainerClassnames = cx(`${rootClassName}__select-container`, {
      [`${rootClassName}__select-container--disabled`]: selectProps.disabled,
      [`${rootClassName}__select-container--readonly`]: readOnly && !selectProps.disabled,
      [`${rootClassName}__select-container--negative`]: negative,
    });

    return (
      <div
        className={cx(rootClassName, className, {
          [`${rootClassName}--negative`]: negative,
        })}
        data-testid={dataTestId}
      >
        {label && (
          <Label
            htmlFor={fieldId}
            className={cx(`${rootClassName}__label`)}
            data-testid={getTestId("label")}
            disabled={selectProps.disabled}
            negative={negative}
          >
            {selectProps.required && <span aria-hidden>*</span>}
            {label}
          </Label>
        )}
        <div className={cx(`${rootClassName}__field-row`)}>
          <div className={selectContainerClassnames}>
            <select
              {...selectProps}
              onChange={readOnly ? undefined : selectProps?.onChange}
              id={fieldId}
              ref={ref}
              data-testid={getTestId("select")}
              aria-describedby={selectProps["aria-describedby"] || helperTextId}
              aria-invalid={selectProps["aria-invalid"] || !!errorText}
              aria-disabled={selectProps["aria-disabled"] || selectProps.disabled}
              aria-readonly={selectProps["aria-readonly"] || (readOnly && !selectProps.disabled)}
              className={cx(`${rootClassName}__select`, {
                [`${rootClassName}__select--error`]: !!errorText,
                [`${rootClassName}__select--is-valid`]: isValid,
                [`${rootClassName}__select--negative`]: negative,
              })}
            >
              {placeholder && (
                <option value={placeholder.value ?? placeholder.label} disabled>
                  {placeholder.label}
                </option>
              )}
              {options.map((optionOrOptionGroup, index) =>
                isOptionGroup(optionOrOptionGroup)
                  ? renderOptionGroup(optionOrOptionGroup, fieldId, index, readOnly)
                  : renderOptionItem(optionOrOptionGroup, fieldId, index, readOnly)
              )}
            </select>
            <div className={cx(`${rootClassName}__end-adornments`)}>
              {loading ? (
                <Spinner
                  key="spinner"
                  disabled={selectProps.disabled}
                  size="xxs"
                  negative={negative}
                  data-testid={getTestId("spinner")}
                />
              ) : (
                <IconChevronDown
                  key="chevron-down-icon"
                  className={cx(`${rootClassName}__select-icon`, {
                    [`${rootClassName}__select-icon--disabled`]: selectProps.disabled,
                    [`${rootClassName}__select-icon--readonly`]: readOnly && !selectProps.disabled,
                    [`${rootClassName}__select-icon--is-valid`]: isValid,
                    [`${rootClassName}__select-icon--negative`]: negative,
                  })}
                  data-testid={getTestId("dropdown-icon")}
                  size="sm"
                />
              )}
              {isValid && (
                <IconCheckCircleFilled
                  key="valid-icon"
                  data-testid={getTestId("valid-icon")}
                  className={cx(`${rootClassName}__valid-icon`)}
                />
              )}
            </div>
            <div
              className={cx(`${rootClassName}__frame`, {
                [`${rootClassName}__frame--negative`]: negative,
              })}
            />
          </div>
        </div>
        {helperTextId && (
          <FieldHelperText
            disabled={selectProps.disabled}
            data-testid={getTestId("helper-text")}
            id={helperTextId}
            negative={negative}
          >
            {helperText}
          </FieldHelperText>
        )}
        {errorText && (
          <FieldErrorText negative={negative} data-testid={getTestId("error-text")}>
            {errorText}
          </FieldErrorText>
        )}
      </div>
    );
  }
);

const isOptionGroup = (option: SelectOption | SelectOptionGroup): option is SelectOptionGroup => {
  // eslint-disable-next-line no-prototype-builtins
  return option.hasOwnProperty("options");
};

const renderOptionGroup = (
  optionGroup: SelectOptionGroup,
  fieldId: string,
  index: number,
  disableOption: boolean
) => {
  const groupKey = `${fieldId}--group--${index}`;

  return (
    <optgroup key={groupKey} label={optionGroup.label}>
      {optionGroup.options.map((option, index) => {
        return renderOptionItem(option, groupKey, index, disableOption);
      })}
    </optgroup>
  );
};

const renderOptionItem = (option: SelectOption, key: string, index: number, disabled: boolean) => {
  return (
    <option
      key={`${key}--option--${index}`}
      value={option.value ?? option.label}
      disabled={disabled}
    >
      {option.label}
    </option>
  );
};

Select.displayName = "Select";
